import React, {useMemo, useState} from "react"
import {useStore, useDispatch} from "../Store"
import {Steps, Hints} from "intro.js-react"
import {makeStyles} from "@material-ui/core/styles"
import {useTranslation} from "react-i18next"
import "../i18n"
import StepsMessage from "./StepsMessage"
import HintsMessage from "./HintsMessage"
import {useMediaQuery} from "@material-ui/core"
import {useTheme} from "../hooks"
import {returnToTop} from "../common"

const useStyles = makeStyles(() => ({
  introjsTooltip: {
    width: (props) => (props.isMobile ? `auto` : `max-content`),
  },
}))

export default function Guide() {
  const {
    store: {locale, themeColor, page, perspective, guideInfo},
  } = useStore()
  const theme = useTheme(themeColor)
  const isMobile = useMediaQuery(theme.breakpoints.down(`xs`))
  const {initialStep, stepsEnabled, hintsEnabled} = guideInfo
  const classes = useStyles({
    isMobile,
  })
  const {dispatch} = useDispatch()
  const {t} = useTranslation()

  //++++++++++++++++++++++++
  //+++ ガイドツアー関連 +++
  //++++++++++++++++++++++++
  const [pageStart, setPageStart] = useState(null)
  const steps = StepsMessage(locale)
  const stepsOptions = useMemo(() => ({
      nextLabel: t(`next`),
      prevLabel: t(`prev`),
      skipLabel: t(`skip`),
      doneLabel: t(`done`),
      tooltipClass: classes.introjsTooltip,
      showProgress: true,
      overlayOpacity: themeColor === `dark` ? 0.8 : 0.2,
    }),
    [classes, locale, themeColor],
  )

  /** ガイド開始時の処理 */
  const stepsStart = (nextStep) => {
    if (!pageStart)
      setPageStart(page)
  }

  /** ガイドステップ切替直前の処理 */
  const stepsBeforeChange = (nextStep) => {
    if (steps[nextStep].returnToTop)
      returnToTop()

    if (page !== steps[nextStep].page)
      return false
  }

  /** ガイドステップ変更がされなかった時の処理 */  
  const stepsPreventChange = async (nextStep) => {
    if (nextStep) { // Complete時はundefined
      nextStep++ // Preventされて戻るので 1 増やす

      dispatch({
        type: `GUIDE_STEPS`,
        stepsEnabled: false,
      })
      dispatch({
        type: `PAGE`,
        page: steps[nextStep].page,
      })

      await new Promise((resolve) => setTimeout(resolve, 1000))

      dispatch({
        type: `GUIDE_INITIAL_STEP`,
        initialStep: nextStep,
      })
      dispatch({
        type: `GUIDE_STEPS`,
        stepsEnabled: true,
      })
    }
  }

  /** 終了時の処理 */
  const stepsExit = () => {
    if (stepsEnabled) { // ガイドが開いている時のみ処理する
      dispatch({
        type: `GUIDE_STEPS`,
        stepsEnabled: false,
      })

      if (pageStart && page !== pageStart) {
        dispatch ({
          type: `PAGE`,
          page: pageStart
        })
      }

      setPageStart(null)
    }
  }

  //++++++++++++++++++
  //+++ Hints 関連 +++
  //++++++++++++++++++
  const hints = HintsMessage(locale, perspective, page)
  const hintsOptions = useMemo(
    () => ({
      hintButtonLabel: t(`gotit`),
      tooltipClass: classes.introjsTooltip,
    }),
    [classes],
  )
  const hintsClose = () => {
    dispatch({
      type: `GUIDE_HINTS`,
      hintsEnabled: false,
    })
  }

  //++++++++++++++++++++++++++++++++++++++++
  //+++ ガイドツアー、及びヘルプのＪＳＸ +++
  //++++++++++++++++++++++++++++++++++++++++
  return (
    <>
      <Steps
        options={stepsOptions}
        enabled={stepsEnabled}
        steps={steps}
        initialStep={initialStep}
        onStart={stepsStart}
        onBeforeChange={stepsBeforeChange}
        onPreventChange={stepsPreventChange}
        onExit={stepsExit}
      />
      <Hints
        options={hintsOptions}
        enabled={hintsEnabled}
        hints={hints}
        onClose={() => hintsClose()}
      />
    </>
  )
}
